import { element, elements } from "./dom";

interface KundDetail {
  title: string;
  copy: string;
}

/** The three hotspots on the Kund photograph, in the order the buttons carry them. */
const KUND_DETAILS: readonly KundDetail[] = [
  {
    title: "The Kund",
    copy: "The recessed centre contains the fire. In this gathering, participants sit around its open sides, with offerings placed within reach.",
  },
  {
    title: "Prepared with care",
    copy: "The stepped edges are decorated by hand with colour and flowers. Look at the preparation chapter to see the care that precedes the ceremony.",
  },
  {
    title: "The kalash",
    copy: "Brass vessels with leaves stand beside the fire. Along with flowers and the arranged materials, they form part of the space prepared for this Yajna.",
  },
];

export interface Scene {
  /** Basename under /assets: `<asset>.mp4` and `<asset>.jpg`. */
  asset: string;
  title: string;
  term: string;
  copy: string;
}

/** The six filmed moments, all from the ASY Team's Kumbh collection. */
// fallow-ignore-next-line unused-export -- exercised directly by its unit test
export const SCENES: readonly Scene[] = [
  {
    asset: "prepare",
    title: "Preparing the space",
    term: "PREPARATION",
    copy: "Colour is placed on the stepped edges. Flowers and vessels are arranged by hand. The space is prepared with care before people gather around the fire.",
  },
  {
    asset: "gather",
    title: "Gathering and settling",
    term: "ATTENTION",
    copy: "People sit around the prepared Kunds and settle together. The transcript describes a pause before the Yajna, inviting participants to sit quietly and close their eyes.",
  },
  {
    asset: "light",
    title: "Agni in the Kund",
    term: "AGNI · FIRE",
    copy: "Agni burns within the Kund. In the translated teaching, its upward movement becomes an invitation to lift our awareness beyond a narrow concern for ourselves.",
  },
  {
    asset: "offer",
    title: "Making an offering",
    term: "AHUTI · SHRADDHA",
    copy: "Ahuti is the offering placed into Agni with the recitation of Vedic mantras and Swaha. The source teaching emphasises Shraddha, faith and devotion, and love in making the offering. Participants follow the guidance of the person leading the Yajna.",
  },
  {
    asset: "together",
    title: "Parikrama around the Kunds",
    term: "PARIKRAMA",
    copy: "Here the participants rise and move around the Kunds. Watch the actual movement in the gathering. The timing and manner of participation are guided on the day.",
  },
  {
    asset: "close",
    title: "Offering reverence",
    term: "AARTI · REVERENCE",
    copy: "The recorded gathering also includes aarti: a lighted lamp is held in reverence, with others standing with joined palms. These moments show participation beyond the offerings into the fire.",
  },
];

const AMBIENCE_VOLUME = 0.4;
const AMBIENCE_UNDER_VOICE = 0.12;

function pad(index: number): string {
  return String(index + 1).padStart(2, "0");
}

function mountKundDetails(): void {
  const title = element("detail-title", HTMLHeadingElement);
  const copy = element("detail-copy", HTMLParagraphElement);
  const number = element("detail-number", HTMLParagraphElement);
  const count = element("detail-count", HTMLSpanElement);
  const hotspots = elements(document, "[data-detail]", HTMLButtonElement);
  let index = 0;

  function show(next: number): void {
    index = (next + KUND_DETAILS.length) % KUND_DETAILS.length;
    const detail = KUND_DETAILS[index];
    if (!detail) {
      return;
    }
    title.textContent = detail.title;
    copy.textContent = detail.copy;
    number.textContent = `DETAIL ${pad(index)} / ${pad(KUND_DETAILS.length - 1)}`;
    count.textContent = `${pad(index)} of ${pad(KUND_DETAILS.length - 1)}`;
    hotspots.forEach((button, i) => {
      button.setAttribute("aria-pressed", String(i === index));
    });
  }

  hotspots.forEach((button) => {
    button.addEventListener("click", () => {
      show(Number(button.dataset.detail));
    });
  });
  element("prev-detail", HTMLButtonElement).addEventListener("click", () => {
    show(index - 1);
  });
  element("next-detail", HTMLButtonElement).addEventListener("click", () => {
    show(index + 1);
  });
}

interface Playback {
  /** Re-evaluates which films should be playing right now. */
  update(): void;
  resume(): void;
}

/**
 * Films play only while on screen, only while the tab is visible, and never
 * when the visitor (or their OS) has asked for less motion. One control
 * pauses everything; a failed autoplay (Low Power Mode) flips to paused
 * rather than erroring.
 */
function mountPlayback(): Playback {
  const reduced = matchMedia("(prefers-reduced-motion: reduce)");
  const motion = element("motion", HTMLButtonElement);
  const videos = elements(document, ".observed-video", HTMLVideoElement);
  const visible = new Set<HTMLVideoElement>();
  let paused = reduced.matches;

  function label(): void {
    motion.textContent = paused ? "Play motion ▷" : "Pause motion Ⅱ";
    motion.setAttribute("aria-pressed", String(paused));
  }

  function update(): void {
    for (const video of videos) {
      if (!paused && !document.hidden && visible.has(video)) {
        video.play().catch(() => {
          paused = true;
          label();
        });
      } else {
        video.pause();
      }
    }
  }

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!(entry.target instanceof HTMLVideoElement)) {
          continue;
        }
        if (entry.isIntersecting) {
          visible.add(entry.target);
        } else {
          visible.delete(entry.target);
        }
      }
      update();
    },
    { threshold: 0.15 },
  );
  videos.forEach((video) => observer.observe(video));

  motion.addEventListener("click", () => {
    paused = !paused;
    label();
    update();
  });
  reduced.addEventListener("change", (event) => {
    paused = event.matches;
    label();
    update();
  });
  label();

  return {
    update,
    resume() {
      if (paused) {
        paused = false;
        label();
        update();
      }
    },
  };
}

function mountScenes(playback: Playback): void {
  const video = element("scene-video", HTMLVideoElement);
  const slow = element("slow", HTMLButtonElement);
  const caption = element("scene-caption", HTMLSpanElement);
  const term = element("scene-term", HTMLParagraphElement);
  const copy = element("scene-copy", HTMLParagraphElement);
  const description = element("scene-description", HTMLDivElement);
  const tabs = elements(document, "[data-scene]", HTMLButtonElement);
  let index = 0;

  function show(next: number, focus = false): void {
    index = (next + SCENES.length) % SCENES.length;
    const scene = SCENES[index];
    if (!scene) {
      return;
    }
    video.pause();
    video.poster = `/assets/${scene.asset}.jpg`;
    video.src = `/assets/${scene.asset}.mp4`;
    video.setAttribute("aria-label", scene.title);
    video.playbackRate = 1;
    slow.setAttribute("aria-pressed", "false");
    slow.textContent = "Look slowly ◷";
    caption.textContent = `${scene.title} · Kumbh`;
    term.textContent = scene.term;
    copy.textContent = scene.copy;
    description.setAttribute("aria-labelledby", `step-${index}`);
    tabs.forEach((tab, i) => {
      tab.setAttribute("aria-selected", String(i === index));
      tab.tabIndex = i === index ? 0 : -1;
    });
    if (focus) {
      tabs[index]?.focus();
    }
    video.load();
    playback.update();
  }

  const moves = new Map<string, number>([
    ["ArrowRight", 1],
    ["ArrowDown", 1],
    ["ArrowLeft", -1],
    ["ArrowUp", -1],
  ]);
  tabs.forEach((tab) => {
    tab.addEventListener("click", () => {
      show(Number(tab.dataset.scene));
    });
    tab.addEventListener("keydown", (event) => {
      const move = moves.get(event.key);
      if (move !== undefined) {
        event.preventDefault();
        show(index + move, true);
      } else if (event.key === "Home" || event.key === "End") {
        event.preventDefault();
        show(event.key === "Home" ? 0 : SCENES.length - 1, true);
      }
    });
  });

  slow.addEventListener("click", () => {
    const slowing = video.playbackRate === 1;
    video.playbackRate = slowing ? 0.5 : 1;
    slow.setAttribute("aria-pressed", String(slowing));
    slow.textContent = slowing ? "Return to pace ▷" : "Look slowly ◷";
    playback.resume();
  });
}

/** Ambience and the spoken invitation are opt-in, start silent, and stop when the tab is hidden. */
function mountAudio(): () => void {
  const ambient = element("ambience", HTMLAudioElement);
  const narration = element("narration", HTMLAudioElement);
  const status = element("audio-status", HTMLDivElement);
  const voice = element("voice", HTMLButtonElement);
  const soundButtons = elements(document, "[data-sound]", HTMLButtonElement);
  let soundOn = false;
  let voiceOn = false;

  function labels(): void {
    for (const button of soundButtons) {
      button.setAttribute("aria-pressed", String(soundOn));
      button.replaceChildren();
      const mark = document.createElement("span");
      mark.setAttribute("aria-hidden", "true");
      mark.textContent = soundOn ? "◉" : "◌";
      button.append(
        mark,
        ` ${soundOn ? "Quiet the gathering" : "Hear the gathering"}`,
      );
    }
    voice.setAttribute("aria-pressed", String(voiceOn));
    voice.textContent = voiceOn
      ? "Ⅱ Pause the invitation"
      : "▷ Listen to the invitation";
  }

  for (const button of soundButtons) {
    button.addEventListener("click", async () => {
      soundOn = !soundOn;
      if (soundOn) {
        ambient.volume = voiceOn ? AMBIENCE_UNDER_VOICE : AMBIENCE_VOLUME;
        try {
          await ambient.play();
        } catch {
          soundOn = false;
          status.textContent = "Audio could not start. Please try again.";
        }
      } else {
        ambient.pause();
      }
      labels();
    });
  }

  voice.addEventListener("click", async () => {
    voiceOn = !voiceOn;
    if (voiceOn) {
      ambient.volume = AMBIENCE_UNDER_VOICE;
      try {
        await narration.play();
      } catch {
        voiceOn = false;
        status.textContent =
          "The invitation could not start. Please try again.";
      }
    } else {
      narration.pause();
      ambient.volume = AMBIENCE_VOLUME;
    }
    labels();
  });

  narration.addEventListener("ended", () => {
    voiceOn = false;
    ambient.volume = AMBIENCE_VOLUME;
    labels();
  });

  labels();

  return () => {
    ambient.pause();
    narration.pause();
    soundOn = false;
    voiceOn = false;
    labels();
  };
}

export function mountExperience(): void {
  mountKundDetails();
  const playback = mountPlayback();
  mountScenes(playback);
  const silence = mountAudio();
  document.addEventListener("visibilitychange", () => {
    playback.update();
    if (document.hidden) {
      silence();
    }
  });
}
